import { useEffect, useState } from 'react';

/**
 * v0.5 Stage C.2 — UI for confirming a column legitimately holds
 * multiple types. Renders below the column-datatype HITL's radio
 * group when the reviewer selects ``mixed_types``. Surfaces a
 * checkbox group of the detector candidates RAS surfaced (the same
 * type tokens that would have been single-type radio choices) so
 * the reviewer can opt in/out of each one. Picks a ``primary`` type
 * defaulting to ``"string"`` if it's in the candidate list, else
 * the last candidate (lowest priority by RAS's ordering).
 *
 * The selection is reported up to ``HitlQueue`` via ``onChange``
 * with a ``{types: string[], primary: string | null}`` payload.
 * The dispatch builder reads the latest payload when the reviewer
 * clicks Confirm.
 *
 * Pre-tick policy
 * ---------------
 * RAS only surfaces the ``mixed_types`` option in the HITL when at
 * least 2 detectors meet ``MIXED_TYPE_PRETICK_THRESHOLD`` (0.20)
 * — so every type token reaching this component is already at
 * meaningful conformity. Pre-tick all of them; reviewer unticks
 * anything they don't want cleansed. A future v0.6 polish surfaces
 * per-detector conformity scores so the reviewer sees the relative
 * strength of each candidate next to its checkbox.
 *
 * Primary-type policy
 * -------------------
 * ``primary`` is the catch-all type when ``"string"`` is in the
 * picked set (the per-row classifier handles that internally).
 * When ``"string"`` is not picked, ``primary`` defaults to the
 * lowest-priority picked type per the tie-breaker order — but
 * this is informational on the UI; the backend resolves
 * unmatched rows to ``mixed_type_unmatched`` regardless.
 */

export interface MixedTypeSelection {
  types: string[];
  primary: string | null;
}

export interface MixedTypeSelectorProps {
  /**
   * Detected types from the RAS HITL options list, with
   * ``mixed_types`` and ``no_format_required`` already filtered
   * out by the parent. Each token is a concrete cleanser-routable
   * type (e.g. ``"date"``, ``"string"``, ``"number"``).
   */
  candidateTypes: string[];
  /** Notified whenever the picked set or primary changes. */
  onChange: (selection: MixedTypeSelection) => void;
  /** True while another HITL action is pending. */
  disabled?: boolean;
}

function pickDefaultPrimary(types: string[]): string | null {
  if (types.length === 0) return null;
  if (types.includes('string')) return 'string';
  // Without ``string``, fall back to the last token RAS surfaced —
  // RAS sorts candidates by descending confidence, so the last
  // entry is the lowest-priority picked type. Caller can override
  // via the radio.
  return types[types.length - 1];
}

export function MixedTypeSelector({
  candidateTypes,
  onChange,
  disabled = false,
}: MixedTypeSelectorProps) {
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(candidateTypes),
  );
  const [primary, setPrimary] = useState<string | null>(
    () => pickDefaultPrimary(candidateTypes),
  );

  // Notify parent on first render so the dispatch builder has a
  // selection ready when the reviewer clicks Confirm without
  // touching a checkbox.
  useEffect(() => {
    onChange({ types: Array.from(picked), primary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePick = (type: string) => {
    const next = new Set(picked);
    let nextPrimary = primary;
    if (next.has(type)) {
      next.delete(type);
      if (primary === type) {
        nextPrimary = pickDefaultPrimary(Array.from(next));
      }
    } else {
      next.add(type);
    }
    setPicked(next);
    setPrimary(nextPrimary);
    onChange({ types: Array.from(next), primary: nextPrimary });
  };

  const onPrimaryChange = (type: string) => {
    setPrimary(type);
    onChange({ types: Array.from(picked), primary: type });
  };

  const pickedList = Array.from(picked);
  const noneSelected = pickedList.length === 0;

  return (
    <fieldset
      disabled={disabled}
      data-testid="mixed-type-selector"
      className="rounded border border-border bg-surface-alt/20 p-3 mt-3 space-y-3"
    >
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted">
        Pick types to cleanse
      </legend>
      <p className="text-xs text-muted">
        Each row will be routed to the cleanser for its detected type.
        Rows that match none of the picked types become anomalies.
      </p>

      <div role="group" aria-label="Picked types" className="flex flex-wrap gap-x-4 gap-y-2">
        {candidateTypes.map((type) => {
          const inputId = `mixed-type-pick-${type}`;
          return (
            <label
              key={type}
              htmlFor={inputId}
              className="inline-flex items-center gap-2 text-sm cursor-pointer"
            >
              <input
                id={inputId}
                type="checkbox"
                checked={picked.has(type)}
                onChange={() => togglePick(type)}
                className="accent-brand-dark"
                data-testid={`mixed-type-checkbox-${type}`}
              />
              <span className="font-mono">{type}</span>
            </label>
          );
        })}
      </div>

      {noneSelected ? (
        <p
          className="text-xs text-danger"
          data-testid="mixed-type-empty-warning"
        >
          Pick at least one type to confirm.
        </p>
      ) : (
        <div
          role="radiogroup"
          aria-label="Primary type (catch-all)"
          className="border-t border-border pt-2 space-y-1"
        >
          <p className="text-xs text-muted">
            Primary type — used as the catch-all for ambiguous rows.
            Defaults to <code>string</code> when picked.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {pickedList.map((type) => {
              const inputId = `mixed-type-primary-${type}`;
              return (
                <label
                  key={type}
                  htmlFor={inputId}
                  className="inline-flex items-center gap-2 text-xs cursor-pointer"
                >
                  <input
                    id={inputId}
                    type="radio"
                    name="mixed-type-primary"
                    value={type}
                    checked={primary === type}
                    onChange={() => onPrimaryChange(type)}
                    className="accent-brand-dark"
                    data-testid={`mixed-type-primary-${type}`}
                  />
                  <span className="font-mono">{type}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </fieldset>
  );
}
